import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Skeleton } from 'antd'
import { TooltipWrapper } from 'components/TooltipWrapper'
import { PERMISSION_CANTILEVER_CORE_ADMIN, useGeckoAuth } from 'features/auth'
import React, { ReactNode, useMemo } from 'react'
import styled from 'styled-components'
import { GeckoOrganization, GeckoSite } from 'types'

const StyledContainer = styled.div`
  padding: 0 0.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid transparent;
  transition: border-color 0.2s ease-in-out;
  border-radius: 0.25rem;
  min-height: 40px;
  width: 100%;
  font-size: 14px;
  line-height: 20px;
  font-weight: medium;
  text-align: left;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const StyledDropdownTrigger = styled(DropdownMenu.Trigger)`
  padding: 0 0.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid transparent;
  transition: border-color 0.2s ease-in-out;
  border-radius: 0.25rem;
  background-color: ${({ theme }) => theme.forecast.defaultButton};
  min-height: 40px;
  width: 100%;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 14px;
  line-height: 20px;
  font-weight: medium;
  cursor: pointer;
  text-align: left;
  border: none;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.hoverButton};
  }

  &:active {
    color: ${({ theme }) => theme.forecast.activeText};
    background-color: ${({ theme }) => theme.forecast.activeButton};
  }

  &:focus-visible {
    outline: none;
    outline-offset: 2px;
  }

  &[data-state='open'] {
    color: ${({ theme }) => theme.forecast.defaultText};
    background-color: ${({ theme }) => theme.forecast.activeButton};
  }
`

const ImageContainer = styled.div`
  width: 32px;
  height: 32px;
  background-color: ${({ theme }) => theme.forecast.defaultButton};
  flex-shrink: 0;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const LabelContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-grow: 1;
  line-height: 16px;
  margin: 0 4px 0 8px;
`
const LabelSpan = styled.span`
  flex-grow: 1;
  max-height: 4.75em;
  text-overflow: ellipsis;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  padding: 0.125rem 0;
`

const TriangleSpan = styled.span`
  flex-shrink: 0;
  flex-grow: 0;
`

const SkeletonContainer = styled.div`
  width: 100%;

  .ant-skeleton {
    width: 80%;
    height: 0.75rem;
    display: block;

    .ant-skeleton-input {
      min-width: 0;
      width: 100%;
      height: 100%;
    }
  }
`

const ButtonContent: React.FC<{
  contentData: GeckoOrganization | GeckoSite | undefined
  isLeftSidebarOpen: boolean
  isSingleItem?: boolean
  isLoading?: boolean
}> = ({ contentData, isLeftSidebarOpen, isSingleItem, isLoading }) => {
  const name = useMemo(() => {
    return contentData?.data.name ?? ''
  }, [contentData])

  const initials = useMemo(() => {
    const filteredName = name.replace(/[^\w\s]/gi, ' ')
    const words = filteredName?.split(' ')
    const initials = words?.map((word) => word.charAt(0).toUpperCase())
    return initials?.join('').slice(0, 3)
  }, [name])

  return (
    <>
      <ImageContainer
        data-tooltip-id={`tooltip-${name}`}
        data-tooltip-content={name}
        data-tooltip-place='right'>
        {isLoading ? (
          <Skeleton.Avatar
            active
            shape='square'
            style={{ borderRadius: '0.125rem' }}
          />
        ) : (
          <span>{initials}</span>
        )}
      </ImageContainer>
      {isLeftSidebarOpen && (
        <LabelContainer>
          {isLoading ? (
            <SkeletonContainer>
              <Skeleton.Input active />
            </SkeletonContainer>
          ) : (
            <>
              <LabelSpan
                data-testid={`${contentData?.data.name}-content-dropdown`}>
                {contentData && contentData.data.name}
              </LabelSpan>
              {!isSingleItem && <TriangleSpan>▾</TriangleSpan>}
            </>
          )}
        </LabelContainer>
      )}
    </>
  )
}

type WrapperComponentProps = {
  children: ReactNode
  disabled?: boolean
  'data-testid'?: string
}

export const DropdownSearchButton: React.FC<{
  contentData: GeckoOrganization | GeckoSite | undefined
  isLeftSidebarOpen: boolean
  disabled?: boolean
  isSingleItem?: boolean
  isLoading?: boolean
  testId?: string
}> = ({
  contentData,
  isLeftSidebarOpen,
  disabled,
  isSingleItem,
  isLoading,
  testId,
}) => {
  const { hasPermissions } = useGeckoAuth()

  const renderDropdownTrigger = (
    WrapperComponent: React.ComponentType<WrapperComponentProps>
  ) =>
    contentData?.data.name && !isLeftSidebarOpen ? (
      <TooltipWrapper side='right' offset={2} label={contentData.data.name}>
        <WrapperComponent disabled={disabled} data-testid={testId}>
          <ButtonContent
            contentData={contentData}
            isLeftSidebarOpen={isLeftSidebarOpen}
            isSingleItem={isSingleItem}
            isLoading={isLoading}
          />
        </WrapperComponent>
      </TooltipWrapper>
    ) : (
      <WrapperComponent disabled={disabled} data-testid={testId}>
        <ButtonContent
          contentData={contentData}
          isLeftSidebarOpen={isLeftSidebarOpen}
          isSingleItem={isSingleItem}
          isLoading={isLoading}
        />
      </WrapperComponent>
    )

  return hasPermissions([PERMISSION_CANTILEVER_CORE_ADMIN]) || !isSingleItem
    ? renderDropdownTrigger(StyledDropdownTrigger)
    : renderDropdownTrigger(StyledContainer)
}
